import React, { useState } from 'react'
import { Mail, Lock, Eye, EyeOff } from 'lucide-react'
import { authService } from '../../services/auth'

interface LoginFormProps {
  onSuccess?: () => void
  onRegisterClick?: () => void
}

export function LoginForm({ onSuccess, onRegisterClick }: LoginFormProps): React.ReactElement {
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [showPassword, setShowPassword] = useState(false)
  const [error, setError] = useState('')
  const [loading, setLoading] = useState(false)

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setError('')
    setLoading(true)

    try {
      const response = await authService.login(email, password)
      authService.setToken(response.token)
      authService.setUser(response.user)
      onSuccess?.()
    } catch (err: any) {
      setError(err.response?.data?.error || 'Login failed')
    } finally {
      setLoading(false)
    }
  }

  const isLoading = loading

  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-100 via-pink-50 to-yellow-50 flex items-center justify-center p-4">
      <form
        onSubmit={handleSubmit}
        className="max-w-md w-full bg-gradient-to-br from-blue-100 to-purple-100 rounded-3xl shadow-xl border-4 border-blue-200 p-10 space-y-6"
      >
        {/* Logo with Emoji */}
        <div className="flex justify-center mb-6">
          <div className="w-20 h-20 bg-gradient-to-br from-purple-300 to-pink-300 rounded-3xl flex items-center justify-center text-white font-bold text-5xl shadow-lg">
            👶
          </div>
        </div>

        {/* Heading */}
        <div className="text-center space-y-2">
          <h1 className="text-4xl font-black text-purple-700">Welcome Back!</h1>
          <p className="text-lg text-pink-600 font-bold">Let's play in the kingdom! 🎮</p>
        </div>

        {/* Email Field */}
        <div className="space-y-2">
          <label className="block text-sm font-bold text-purple-700">Email</label>
          <div className="flex items-center bg-white border-3 border-pink-200 rounded-2xl px-4 py-4 focus-within:border-pink-400 focus-within:ring-2 focus-within:ring-pink-200">
            <Mail className="w-6 h-6 text-pink-400 mr-3" />
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="your@email.com"
              className="flex-1 outline-none text-gray-900 bg-transparent font-semibold"
              required
            />
          </div>
        </div>

        {/* Password Field */}
        <div className="space-y-2">
          <label className="block text-sm font-bold text-purple-700">Password</label>
          <div className="flex items-center bg-white border-3 border-pink-200 rounded-2xl px-4 py-4 focus-within:border-pink-400 focus-within:ring-2 focus-within:ring-pink-200">
            <Lock className="w-6 h-6 text-pink-400 mr-3" />
            <input
              type={showPassword ? 'text' : 'password'}
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="••••••••"
              className="flex-1 outline-none text-gray-900 bg-transparent font-semibold"
              required
            />
            <button
              type="button"
              onClick={() => setShowPassword(!showPassword)}
              className="text-pink-400 hover:text-pink-500 transition"
            >
              {showPassword ? <EyeOff className="w-6 h-6" /> : <Eye className="w-6 h-6" />}
            </button>
          </div>
        </div>

        {/* Demo Box */}
        <div className="bg-gradient-to-r from-yellow-100 to-orange-100 border-3 border-orange-300 rounded-2xl p-4 space-y-2">
          <p className="text-sm font-black text-orange-700">🎪 Demo Account:</p>
          <p className="text-sm font-bold text-orange-700">📧 parent1@babykingdom.com</p>
          <p className="text-sm font-bold text-orange-700">🔑 password123</p>
        </div>

        {/* Error Display */}
        {error && (
          <div className="bg-gradient-to-r from-red-100 to-pink-100 border-3 border-red-300 rounded-2xl px-4 py-3">
            <p className="text-red-700 font-bold text-sm">⚠️ {error}</p>
          </div>
        )}

        {/* Submit Button */}
        <button
          type="submit"
          disabled={isLoading}
          className="w-full py-4 bg-gradient-to-r from-pink-400 to-purple-400 text-white font-black text-lg rounded-2xl hover:shadow-xl transition disabled:opacity-50 disabled:cursor-not-allowed transform hover:scale-105"
        >
          {isLoading ? '⏳ Signing in...' : '🚀 Sign In'}
        </button>

        {/* Register Link */}
        <div className="text-center pt-4 border-t-3 border-pink-300">
          <p className="text-purple-700 font-bold text-lg">
            No account yet?{' '}
            <button
              type="button"
              onClick={onRegisterClick}
              className="text-pink-600 hover:text-pink-700 font-black underline"
            >
              Create one! 🎉
            </button>
          </p>
        </div>
      </form>
    </div>
  )
}
